import React, { useState, useEffect, useRef } from 'react';
import * as configuracionApiService from '@/services/configuracionApiService';
import Button from '@/components/common/Button';
import Card from '@/components/common/Card';
import { FaUpload, FaTrash, FaSave } from 'react-icons/fa';
import toastService from '@/utils/toastService';
import { useConfiguracion } from '@/context/ConfiguracionContext';
import { useSimpleEmpresa } from '@/hooks/useSimpleEmpresa';

/**
 * Componente para gestionar el logo de la aplicación
 * @returns {JSX.Element} - Componente de mantenedor de logo
 */
const LogoMantenedor = () => {
  const [logoUrl, setLogoUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const { configuracion, reloadConfiguracion } = useConfiguracion();
  const { empresaActual } = useSimpleEmpresa();

  // Cargar logo al iniciar
  useEffect(() => {
    if (empresaActual?.id) {
      fetchLogo();
    }
  }, [empresaActual?.id]);

  // Función para cargar el logo
  const fetchLogo = async () => {
    try {
      setLoading(true);
      const config = await configuracionApiService.getConfiguracion(empresaActual?.id);
      if (config && config.Logo) {
        setLogoUrl(config.Logo);
      }
    } catch (error) {
      console.error('Error al cargar el logo:', error);
      // No mostrar error si simplemente no hay configuración
      if (!error.message?.includes('No se encontraron configuraciones')) {
        toastService.error('Error al cargar el logo');
      }
    } finally {
      setLoading(false);
    }
  };

  // Función para subir un nuevo logo
  const handleUploadLogo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar que sea una imagen
    if (!file.type.startsWith('image/')) {
      toastService.error('El archivo debe ser una imagen');
      return;
    }

    // Validar tamaño máximo (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toastService.error('La imagen no debe superar los 2MB');
      return;
    }

    try {
      setUploading(true);
      const logoUrl = await configuracionApiService.uploadLogo(file, empresaActual?.id);
      setLogoUrl(logoUrl);
      
      // Recargar la configuración para actualizar el contexto
      await reloadConfiguracion();
      
      toastService.success('Logo subido correctamente');
    } catch (error) {
      console.error('Error al subir el logo:', error);
      toastService.error('Error al subir el logo');
    } finally {
      setUploading(false);
    }
  };

  // Función para eliminar el logo
  const handleDeleteLogo = async () => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar el logo?')) {
      return;
    }

    try {
      setLoading(true);
      await configuracionApiService.setConfiguracion({
        clave: 'logo_empresa_base64',
        valor: null,
        tipo: 'string',
        descripcion: 'Logo de la empresa en base64',
        empresa_id: empresaActual?.id,
        editable_por_taller: false
      });
      setLogoUrl(null);
      
      // Recargar la configuración para actualizar el contexto
      await reloadConfiguracion();
      
      toastService.success('Logo eliminado correctamente');
    } catch (error) {
      console.error('Error al eliminar el logo:', error);
      toastService.error('Error al eliminar el logo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <p className="mb-5 bg-blue-900/30 p-3 rounded-lg border border-blue-800/50 text-sm text-blue-300">
        El logo se mostrará en la pantalla de inicio de sesión y en el sidebar de la aplicación.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-10 gap-6">
        {/* Sección 1: Logo actual, texto y botones (40%) */}
        <Card className="h-full md:col-span-4">
          <div className="p-6 flex flex-col h-full">
            <h2 className="text-xl font-semibold mb-4">Logo actual</h2>
            
            <div className="bg-gray-800 rounded-lg p-10 flex items-center justify-center mb-8">
              {loading ? (
                <div className="animate-pulse bg-gray-700 h-52 w-96 rounded"></div>
              ) : logoUrl ? (
                <img 
                  src={logoUrl} 
                  alt="Logo" 
                  className="max-h-60 max-w-full object-contain" 
                />
              ) : (
                <div className="text-gray-500 text-center">
                  <p className="mb-2">No hay logo configurado</p>
                  <p className="text-sm">Sube un logo para que aparezca en el login y el sidebar</p>
                </div>
              )}
            </div>
            
            <div className="mb-auto mt-auto">
              <p className="text-gray-400 text-sm">
                Recomendaciones:
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Formato PNG o SVG con fondo transparente</li>
                  <li>Tamaño recomendado: 200x80 píxeles</li>
                  <li>Tamaño máximo: 2MB</li>
                </ul>
              </p>
            </div>
            
            <div className="flex gap-3 justify-end pt-4 border-t border-gray-700">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleUploadLogo}
                accept="image/*"
                className="hidden"
              />
              {logoUrl && (
                <Button
                  variant="danger"
                  onClick={handleDeleteLogo}
                  icon={<FaTrash />}
                  disabled={uploading || loading}
                  size="sm"
                  className="px-3 py-1.5"
                >
                  Eliminar logo
                </Button>
              )}
              
              <Button
                variant="primary"
                onClick={() => fileInputRef.current.click()}
                icon={<FaUpload />}
                loading={uploading}
                disabled={uploading || loading}
                size="sm"
                className="px-3 py-1.5"
              >
                {uploading ? 'Subiendo...' : 'Subir logo'}
              </Button>
              
            </div>
          </div>
        </Card>
        
        {/* Sección 2: Vistas previas (60%) */}
        <Card className="h-full md:col-span-6">
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">Vista previa</h2>
            
            {/* Vista móvil: Solo muestra la vista previa del login */}
            <div className="block md:hidden">
              <div className="bg-gray-900 p-4 rounded-lg">
                <h4 className="text-sm text-gray-400 mb-3">Login</h4>
                <div className="flex flex-col justify-between items-center p-4 bg-gray-900 rounded-lg">
                  {/* Logo */}
                  <div className="w-full flex justify-center mb-4">
                    {logoUrl ? (
                      <img 
                        src={logoUrl} 
                        alt="Logo" 
                        className="max-h-16 max-w-full object-contain" 
                      />
                    ) : (
                      <div className="w-32 h-12 bg-gray-700 rounded animate-pulse"></div>
                    )}
                  </div>
                  
                  {/* Nombre del taller */}
                  <div className="w-full text-center mb-6">
                    <span className="text-2xl font-bold text-blue-400">
                      {configuracion?.NombreTaller || 'Taller App'}
                    </span>
                  </div>
                  
                  {/* Botón de ingreso */}
                  <div className="w-full flex justify-center">
                    <button className="bg-blue-600 hover:bg-blue-700 text-white text-sm w-full px-6 py-3 rounded-md">
                      Ingresar al Sistema
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Vista desktop: Muestra ambas vistas previas */}
            <div className="hidden md:grid md:grid-cols-1 md:gap-6">
              <div className="bg-gray-900 p-4 rounded-lg">
                <h4 className="text-sm text-gray-400 mb-3">Login</h4>
                <div className="flex flex-row justify-between items-center p-4 bg-gray-900 rounded-lg">
                  {/* Logo a la izquierda */}
                  <div className="w-1/4 flex justify-start">
                    {logoUrl ? (
                      <img 
                        src={logoUrl} 
                        alt="Logo" 
                        className="max-h-16 max-w-full object-contain" 
                      />
                    ) : (
                      <div className="w-32 h-12 bg-gray-700 rounded animate-pulse"></div>
                    )}
                  </div>
                  
                  {/* Nombre del taller en el centro */}
                  <div className="w-2/4 text-center">
                    <span className="text-2xl font-bold text-blue-400">
                      {configuracion?.NombreTaller || 'Taller App'}
                    </span>
                  </div>
                  
                  {/* Botón de ingreso a la derecha */}
                  <div className="w-1/4 flex justify-end">
                    <button className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-6 py-3 rounded-md">
                      Ingresar al Sistema
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-900 p-4 rounded-lg">
                <h4 className="text-sm text-gray-400 mb-3">Sidebar</h4>
                <div className="flex items-center justify-start p-4 bg-[#192231] rounded-lg">
                  {logoUrl ? (
                    <img 
                      src={logoUrl} 
                      alt="Logo" 
                      className="max-h-12 max-w-full object-contain" 
                    />
                  ) : (
                    <div className="text-lg font-bold text-white">{configuracion?.NombreTaller || 'Taller App'}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default LogoMantenedor;
